import { useRef, useState, useCallback, useEffect } from 'react';

export const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return { mime: '', ext: 'webm' };
  if (MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus')) return { mime: 'audio/webm;codecs=opus', ext: 'webm' };
  if (MediaRecorder.isTypeSupported?.('audio/webm')) return { mime: 'audio/webm', ext: 'webm' };
  if (MediaRecorder.isTypeSupported?.('audio/mp4')) return { mime: 'audio/mp4', ext: 'mp4' };
  return { mime: '', ext: 'webm' };
};

/**
 * Voice capture for Déesse.
 * - single(): record one utterance, auto-stop on 1.4s silence (after speech) or maxMs.
 * - loop(ms): record fixed-length standalone chunks continuously (Mode B).
 * onBlob(blob, ext) is called for every finished recording. level (0..1) for the avatar.
 */
export function useVoiceCapture(onBlob) {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const streamRef = useRef(null);
  const mrRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const loopRef = useRef(false);
  const timerRef = useRef(null);
  const cbRef = useRef(onBlob);
  cbRef.current = onBlob;

  const cleanup = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);
    try { ctxRef.current?.close(); } catch { /* noop */ }
    ctxRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    mrRef.current = null;
    setLevel(0);
    setRecording(false);
  }, []);

  const getStream = async () => {
    if (streamRef.current) return streamRef.current;
    const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    streamRef.current = s;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    try { await ctx.resume(); } catch { /* noop */ }
    const src = ctx.createMediaStreamSource(s);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    src.connect(analyser);
    ctxRef.current = ctx;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      setLevel(Math.min(1, rms * 4));
      lastRms.current = rms;
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return s;
  };
  const lastRms = useRef(0);

  const recordOnce = (stream, { maxMs, silenceStop }) => new Promise((resolve) => {
    const { mime, ext } = pickMime();
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    mr.onstop = () => resolve({ blob: new Blob(chunks, { type: mime || 'audio/webm' }), ext });
    mrRef.current = mr;
    mr.start();
    const started = Date.now();
    let spoke = false;
    let silentSince = null;
    const check = () => {
      if (mr.state !== 'recording') return;
      const rms = lastRms.current;
      if (rms > 0.012) { spoke = true; silentSince = null; }
      else if (spoke) { silentSince = silentSince || Date.now(); }
      const elapsed = Date.now() - started;
      if (elapsed >= maxMs || (silenceStop && spoke && silentSince && Date.now() - silentSince > 1600)) {
        mr.stop();
        return;
      }
      timerRef.current = setTimeout(check, 120);
    };
    check();
  });

  const single = useCallback(async (maxMs = 20000) => {
    try {
      const s = await getStream();
      setRecording(true);
      const { blob, ext } = await recordOnce(s, { maxMs, silenceStop: true });
      cleanup();
      await cbRef.current?.(blob, ext, 'single');
      return true;
    } catch (e) {
      cleanup();
      throw e;
    }
  }, [cleanup]);

  const loop = useCallback(async (chunkMs = 20000) => {
    const s = await getStream();
    loopRef.current = true;
    setRecording(true);
    while (loopRef.current && streamRef.current) {
      const { blob, ext } = await recordOnce(s, { maxMs: chunkMs, silenceStop: false });
      cbRef.current?.(blob, ext, 'loop');
    }
    cleanup();
  }, [cleanup]);

  const stop = useCallback(() => {
    loopRef.current = false;
    if (mrRef.current && mrRef.current.state === 'recording') mrRef.current.stop();
    else cleanup();
  }, [cleanup]);

  useEffect(() => () => { loopRef.current = false; cleanup(); }, [cleanup]);

  return { recording, level, single, loop, stop };
}
